import React, { useState } from "react";
import { api, errMsg } from "../lib/api";
import { Btn } from "./UI";
import { Upload, Loader2, X, Check, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";

const HEADERS = ["candidate_name", "display_name", "entity_type", "state", "office", "district", "official_email", "website"];
const SAMPLE = "candidate_name,display_name,entity_type,state,office,district,official_email,website\nJane Smith,Smith for Congress,candidate_committee,TX,U.S. House,21,info@smithforcongress.com,smithforcongress.com\nJohn Doe,,candidate_committee,OH,State Senate,4,,";

const parseCsv = (text) => {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return { error: "Paste a header row plus at least one candidate row" };
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  if (!headers.includes("candidate_name") && !headers.includes("display_name")) {
    return { error: "Header row must include candidate_name or display_name" };
  }
  const rows = lines.slice(1).map((line) => {
    const cells = line.split(",");
    const row = {};
    headers.forEach((h, i) => { if (HEADERS.includes(h)) row[h] = (cells[i] || "").trim(); });
    return row;
  }).filter((r) => (r.candidate_name || r.display_name));
  if (rows.length === 0) return { error: "No valid rows found" };
  if (rows.length > 50) return { error: "Max 50 clients per import — split the file" };
  return { rows };
};

const STATUS_STYLE = {
  created: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  duplicate: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  error: "text-rose-300 border-rose-400/30 bg-rose-400/10",
};

export const BulkOnboardButton = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const parsed = csv.trim() ? parseCsv(csv) : null;

  const submit = async () => {
    if (!parsed?.rows) return;
    setBusy(true);
    try {
      const { data } = await api.post("/political-os/agency/bulk-onboard", { rows: parsed.rows });
      setResults(data);
      toast.success(`${data.created} client workspace${data.created === 1 ? "" : "s"} created`);
    } catch (e) { toast.error(errMsg(e, "Bulk import failed")); }
    finally { setBusy(false); }
  };

  const close = () => {
    setOpen(false);
    if (results?.created && onDone) onDone();
    setResults(null); setCsv("");
  };

  return (
    <>
      <Btn variant="secondary" onClick={() => setOpen(true)} data-testid="bulk-onboard-btn">
        <Upload className="w-4 h-4" /> Bulk add clients
      </Btn>
      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-app border border-white/10 rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-onboard-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-2xl font-bold">Bulk add clients</h3>
              <button onClick={close} className="text-slate-500 hover:text-white" data-testid="bulk-onboard-close"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              Paste a CSV of candidates — each row becomes a ready-to-run client workspace with you as consultant. FEC candidates are auto-matched to their race when possible.
            </p>
            {!results ? (
              <>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">CSV (headers: {HEADERS.join(", ")})</span>
                  <div className="flex items-center gap-3">
                    <label className="text-[10px] text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1 cursor-pointer">
                      <Upload className="w-3 h-3" /> Upload .csv file
                      <input type="file" accept=".csv,text/csv,text/plain" className="hidden" data-testid="bulk-csv-file"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const reader = new FileReader();
                          reader.onload = () => setCsv(String(reader.result || ""));
                          reader.onerror = () => toast.error("Could not read that file");
                          reader.readAsText(f);
                          e.target.value = "";
                        }} />
                    </label>
                    <button onClick={() => { navigator.clipboard?.writeText(SAMPLE).catch(() => {}); setCsv(SAMPLE); }} data-testid="bulk-sample-btn"
                      className="text-[10px] text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Use sample</button>
                  </div>
                </div>
                <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={8} data-testid="bulk-csv-input"
                  placeholder={SAMPLE}
                  className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-3 py-2.5 text-xs font-mono text-white placeholder-slate-600 focus:border-cyan-400/60 focus:outline-none" />
                {parsed?.error && (
                  <div className="mt-2 text-xs text-rose-300 flex items-center gap-1.5" data-testid="bulk-parse-error">
                    <AlertTriangle className="w-3.5 h-3.5" /> {parsed.error}
                  </div>
                )}
                {parsed?.rows && (
                  <div className="mt-2 text-xs text-emerald-300" data-testid="bulk-parse-ok">
                    {parsed.rows.length} client{parsed.rows.length === 1 ? "" : "s"} ready to import
                  </div>
                )}
                <div className="mt-4 flex justify-end gap-2">
                  <Btn variant="secondary" onClick={close}>Cancel</Btn>
                  <Btn onClick={submit} disabled={!parsed?.rows || busy} data-testid="bulk-submit-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Create {parsed?.rows?.length || 0} workspaces
                  </Btn>
                </div>
              </>
            ) : (
              <div data-testid="bulk-results">
                <div className="text-sm text-emerald-300 font-semibold mb-3 flex items-center gap-2">
                  <Check className="w-4 h-4" /> {results.created} created · {results.results.length - results.created} skipped
                </div>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {results.results.map((r) => (
                    <div key={r.row} className="flex items-center justify-between gap-3 text-xs p-2.5 rounded-lg border border-white/5 bg-white/[0.02]" data-testid={`bulk-result-${r.row}`}>
                      <span className="min-w-0 truncate text-slate-300">{r.name || `Row ${r.row}`}{r.fec_matched ? " · FEC matched" : ""}</span>
                      <span className={`shrink-0 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLE[r.status] || ""}`}>
                        {r.status}{r.message ? "" : ""}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-end">
                  <Btn onClick={close} data-testid="bulk-done-btn">Done</Btn>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
